import { useState, useEffect, CSSProperties } from "react";
import ModalSymphony from "../../elements/ModalSymphony";
import { SocialConnectType } from "modules/Const";
import InputSymphony from "../../elements/InputSymphony";
import ButtonSymphony from "../../elements/ButtonSymphony";
import { clsx, makeKey, styx } from "modules/Utils";
import Colors from "modules/Colors";
import { millify } from 'millify'
import Loader from "elements/Loader";
import useCurrentBrand from "Hooks/useCurrentBrand";
import { connectToBrand_api, getInstagramBusinessOrCreatorAccount_api } from "api/brandApi";
import { getChannelIdFromUrl, queryYouTubeApi } from "helpers/YouTube";
import { queryAudiomackArtists_api } from "components/connect/Audiomack";
import { getNewSpotifyToken_api, searchSpotifyArtists_api } from "api/spotifyApi";
import useScreen from "Hooks/useScreen";
import { fetchSoundCloudArtists_api } from "api/soundcloudApi";
import { AnySocialConnectProfile, AudiomackArtistProfile, ConnectedInstagramAccountType, DeezerArtistProfile, FacebookPageType, InstagramBusinessAccountType, InstagramProfileForOnboarding, SoundcloudArtistProfile, SpotifyArtistInBrandConnections, SpotifyArtistSearchResponse, TikTokArtist, TikTokProfileForOnboarding, YouTubeChannel } from "modules/Types";
import { queryDeezerArtists_api, queryDeezerArtistsById_api } from "api/deezerApi";
import { toastError, toastSuccess } from "modules/Toast";
import { setUserProperty } from "analytics";
import { track } from "analytics";
import Intercom from 'helpers/Intercom';
import useFacebookConnections from "Hooks/useFacebookConnections";
import NotFoundInstaUser from "components/connect/NotFoundInstaUser";
import { useDashboard } from "Hooks/useDashboard";
import AlreadyExistingArtistModal from "./AlreadyExistingArtistModal";
import axios from "axios";
import Icon from "elements/Icon";
import tikTokPicture from "assets/images/tiktok.svg";

const ICON_SIZE = 50;
const THROTTLED_MODE = true;

/** Type for Facebook error states */
type FacebookErrorState = 'PERMISSIONS_ERROR' | 'NO_PAGES_ERROR';

const ERROR_STATES: Record<FacebookErrorState, { message: string }> = {
    PERMISSIONS_ERROR: {
        message: 'We can\'t load your Facebook Pages because we don\'t have permission. Click the button below to authorize access to your pages.'
    },
    NO_PAGES_ERROR: {
        message: 'It looks like you don\'t have any Facebook pages associated with your account. Please create a new Facebook page by following the instructions at the link.'
    }
};

/** Interface for formatted social connect data */
interface FormattedSocialConnectData {
    /** Unique identifier for the connection */
    id?: string | number;
    /** Display title */
    title: string;
    /** Username if applicable */
    username: string;
    /** Number of followers */
    followers: number;
    /** Profile/thumbnail image URL */
    image: string;
    /** Profile URL */
    url?: string;
}

/** Props for the social connect modal component */
interface SocialConnectModalProps {
    /** Additional class name */
    className: string;
    /** Additional inline styles */
    style?: CSSProperties;
    /** Whether the modal is open */
    isOpen: boolean;
    /** Callback when modal is closed */
    onClose: () => void;
    onConnect?: (connectedProfile: AnySocialConnectProfile) => void;
    socialConnectType: SocialConnectType;
    /** Whether to use direct connect flow */
    isDirectConnect?: boolean;
    /** Whether to validate business account */
    validateBusinessAccount?: boolean;
    isConnectToBrand?: boolean;
}

/** Type for handling social connect data */
type SocialConnectData =
    AudiomackArtistProfile |
    DeezerArtistProfile |
    SoundcloudArtistProfile |
    SpotifyArtistSearchResponse |
    YouTubeChannel |
    FacebookPageType |
    ConnectedInstagramAccountType |
    TikTokArtist;

const SocialConnectModal = ({
    className,
    style,
    isOpen,
    onClose,
    onConnect,
    socialConnectType = SocialConnectType.SPOTIFY,
    isDirectConnect = false,
    validateBusinessAccount = false,
    isConnectToBrand = true,
}: SocialConnectModalProps) => {
    const { currentBrand, setCurrentBrand } = useCurrentBrand(); // NOTE: it's possible for brand to not exist for onboarding?
    const { mobileView } = useScreen();

    const {
        getDashboardData,
        isPlatformConnected
    } = useDashboard()
    const {
        shouldShowLoginButton,
        setFacebookPage,
        isLoadingFacebookPages: isLoadingPages,
        facebookPages,
        getFacebookPages: loadPages,
        loggedInFacebookUser: loggedInUser,
        reRequestPermissions,
        renderFacebookLoginButton,
    } = useFacebookConnections();

    let greetingSocialConnectTitle = 'your';
    if (currentBrand) {
        greetingSocialConnectTitle = `${currentBrand.name}'s`;
    }

    const connectionOptions: Record<
        SocialConnectType, {
            title: string;
            subtitle: string;
            footer?: string | JSX.Element;
            serviceName: string;
            inputPlaceholder?: string;
            followActionName?: string;
            errorComponent?: JSX.Element | string
        }
    > = {
        [SocialConnectType.SPOTIFY]: {
            title: 'Spotify',
            subtitle: `What is the name for ${greetingSocialConnectTitle} Spotify account?`,
            serviceName: 'spotify_artist', // NOTE: comes back as spotify_artist_page from BE
            inputPlaceholder: 'Enter your artist name or Spotify URL',
            followActionName: 'followers',
        },
        [SocialConnectType.YOUTUBE]: {
            title: 'YouTube',
            subtitle: `What is the YouTube channel URL for ${greetingSocialConnectTitle} YouTube account?`,
            serviceName: 'youtube_channel',
            inputPlaceholder: 'Enter your YouTube channel URL',
            followActionName: 'subscribers',
        },
        [SocialConnectType.INSTAGRAM]: {
            title: 'Instagram',
            subtitle: `What is the username for ${greetingSocialConnectTitle} Instagram account?`,
            serviceName: 'xxx', // NOTE: determined in getServiceNameForConnectToBrand()
            inputPlaceholder: 'Enter your Instagram username or URL.',
            followActionName: 'followers',
            errorComponent: <NotFoundInstaUser />
        },
        [SocialConnectType.FACEBOOK]: {
            title: 'Facebook',
            subtitle: `Select ${greetingSocialConnectTitle} Facebook Page.`,
            serviceName: 'facebook_page',
            followActionName: 'followers',
        },
        [SocialConnectType.SOUNDCLOUD]: {
            title: 'SoundCloud',
            subtitle: `What is the name for ${greetingSocialConnectTitle} SoundCloud account?`,
            serviceName: 'soundcloud', // NOTE: comes back as soundcloud_artist_page from BE
            followActionName: 'followers',
            inputPlaceholder: 'Enter your SoundCloud name or URL.',
        },
        [SocialConnectType.AUDIOMACK]: {
            title: 'Audiomack',
            subtitle: `What is the name for ${greetingSocialConnectTitle} Audiomack account?`,
            serviceName: 'audiomack',
            followActionName: 'followers',
            inputPlaceholder: 'Enter your Audiomack name or URL.',
        },
        [SocialConnectType.DEEZER]: {
            title: 'Deezer',
            subtitle: `What is the name for ${greetingSocialConnectTitle} Deezer account?`,
            serviceName: 'deezer',
            followActionName: 'followers',
            inputPlaceholder: 'Enter your Deezer name or URL.',
        },
        [SocialConnectType.TIKTOK]: {
            title: 'TikTok',
            subtitle: `What is the username for ${greetingSocialConnectTitle} TikTok account?`,
            serviceName: 'tiktok',
            followActionName: 'followers',
            inputPlaceholder: 'Enter your TikTok username.',
        },
    };

    const socialConnectTitle = connectionOptions[socialConnectType].title;
    const socialConnectModalTitle = `Connect ${socialConnectTitle}`;
    const socialConnectSubtitle = connectionOptions[socialConnectType].subtitle || `What is the username or URL for ${greetingSocialConnectTitle} ${socialConnectTitle} account?`
    const socialConnectFooter = connectionOptions[socialConnectType].footer;
    const serviceName = connectionOptions[socialConnectType].serviceName;
    const serviceInputPlaceholder = connectionOptions[socialConnectType].inputPlaceholder ?? `Enter your ${socialConnectTitle} username.`;
    const serviceFollowActionName = connectionOptions[socialConnectType].followActionName ?? 'followers';

    const isConnectList = (validateBusinessAccount && socialConnectType === SocialConnectType.INSTAGRAM) || !isDirectConnect;

    const [searchText, setSearchText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [searchResultsList, setSearchResultsList] = useState<SocialConnectData[]>([]);
    const [isLoadingSearchResults, setIsLoadingSearchResults] = useState(false);
    const [inputError, setInputError] = useState<string | null>(null);
    const [currentlySelectedSearchResultId, setCurrentlySelectedSearchResultId] = useState<any>(null);
    const [fetchedSocialData, setFetchedSocialData] = useState<SocialConnectData | null>(null);
    const [fetchedFormattedSocialData, setFetchedFormattedSocialData] = useState<FormattedSocialConnectData | null>(null);
    const [isErrorComponentVisible, setIsErrorComponentVisible] = useState(false);

    const [openAlreadyExistModal, setOpenAlreadyExistModal] = useState(false);
    const [alreadyExistArtist, setAlreadyExistArtist] = useState<SpotifyArtistInBrandConnections | null>(null);


    useEffect(() => {
        if (socialConnectType === SocialConnectType.FACEBOOK
            && facebookPages.length === 0
        ) {
            loadPages();
        }
    }, [socialConnectType]);

    useEffect(() => {
        setInputError(null);
        setIsErrorComponentVisible(false);
    }, [searchText]);

    const isValidYouTubeUrl = (url: string): boolean => {
        const regex = /^(https?:\/\/)?(www\.)?youtube\.com\/((@[a-zA-Z0-9_-]+)|(channel|c|user)\/[a-zA-Z0-9_-]+)$/;
        return regex.test(url);
    }

    const getServiceNameForConnectToBrand = () => {
        let newServiceName = serviceName;
        switch (socialConnectType) {
            case SocialConnectType.FACEBOOK:
                if (validateBusinessAccount) {
                    newServiceName = 'logged_in_fb_user';
                } else {
                    newServiceName = 'facebook_page';
                }
                break;
            case SocialConnectType.INSTAGRAM:
                if (validateBusinessAccount) {
                    newServiceName = 'business_discovery_instagram';
                } else {
                    newServiceName = 'instagram_page';
                }
                break;
        }

        return newServiceName;
    }

    const extractYouTubeChannelIdentifier = (url: string) => {
        const parts = url.split('/');
        const searchedBy = parts[parts.length - 2];
        const lastPart = parts[parts.length - 1];

        const newYoutubeUsername = lastPart.startsWith('@');

        return { searchedBy, identifier: lastPart, newYoutubeUsername };
    }

    const fetchAudiomack = async (): Promise<AudiomackArtistProfile[]> => {
        // NOTE: state is already loading because it's getting a list
        const searchTextLocal = searchText.trim();
        const res = await queryAudiomackArtists_api(searchTextLocal);
        const audiomackResults = res || [];
        return audiomackResults;
    }

    /** Fetches Deezer artist data from either a direct URL or search query */
    const fetchDeezer = async (): Promise<DeezerArtistProfile[]> => {
        // Check if input is a Deezer artist URL
        const pattern = /^https?:\/\/(?:www\.)?deezer\.com\/(?:\w+\/)?artist\/\d+/
        const deezerId = searchText.match(/\/artist\/([^/]+)/)

        let deezerResults;
        if (pattern.test(searchText) && deezerId) {
            const res = await queryDeezerArtistsById_api(deezerId[1]);
            deezerResults = res || [];
        } else {
            const res = await queryDeezerArtists_api(searchText);
            deezerResults = res || [];
        }

        if (!deezerResults.length) {
            throw 'No results found. Please try again.';
        }

        return deezerResults;
    }

    const fetchInstagramBasic = async (searchText: string): Promise<InstagramProfileForOnboarding> => {
        let username = searchText.trim().toLowerCase();

        if (username.startsWith('@')) {
            username = username.substring(1);
        }

        const igProfile = {
            username,
            url: `https://instagram.com/${username}`
        }

        return igProfile;
    }

    const fetchInstagramBusinessAccount = async (): Promise<any[]> => {
        const searchTextLocal = searchText.trim().toLowerCase();
        const { data: igProfile } = await getInstagramBusinessOrCreatorAccount_api(currentBrand?.id, {
            username: searchTextLocal
        });

        if (!igProfile) {
            setIsErrorComponentVisible(true);
            throw 'No results found. Please check the spelling of your Instagram username and try again.';
        }

        return [igProfile];
    }

    const fetchSoundCloud = async (): Promise<SoundcloudArtistProfile[]> => {
        // NOTE: state is already loading because it's getting a list
        const searchTextLocal = searchText.trim();

        // Extract username from URL or use direct input
        const soundcloudUsername = searchTextLocal.match(/(?:on\.|)soundcloud\.com\/([^/]+)/)?.[1] || searchTextLocal;

        const res = await fetchSoundCloudArtists_api(soundcloudUsername);
        const soundCloudResults = res || [];

        if (!soundCloudResults.length) {
            throw 'No results found. Please try your SoundCloud username.';
        }

        return soundCloudResults;
    }

    const fetchSpotify = async (): Promise<SpotifyArtistSearchResponse[]> => {
        const res = await searchSpotifyArtists_api(searchText);
        const spotifyResults = res || [];
        return spotifyResults;
    }

    const fetchTikTokBasic = async (): Promise<TikTokArtist> => {
        let username = searchText.trim().toLowerCase();

        if (username.startsWith('https://')) {
            throw 'Please enter a TikTok username, not a URL.';
        }

        if (username.includes('tiktok.com/')) {
            throw 'Please enter a TikTok username, not a URL.';
        }

        if (username.startsWith('@')) {
            username = username.substring(1);
        }

        return {
            username,
            url: `https://www.tiktok.com/@${username}`,
        };
    }

    /** 
     * Fetches YouTube channel data based on search text input
     * Handles different URL formats including channel ID, username and handle
     * Sets search results in state after fetching data
     */
    const fetchYouTube = async (): Promise<YouTubeChannel[]> => {
        // NOTE: state is already loading because it's getting a list
        const searchTextLocal = searchText.trim();
        console.log('searchTextLocal', searchTextLocal)
        if (THROTTLED_MODE && !isValidYouTubeUrl(searchTextLocal)) {
            setSearchResultsList([]);
            console.log('setting search results to empty')
            throw "Please enter a valid YouTube channel URL."
        }

        const { identifier, searchedBy, newYoutubeUsername } = extractYouTubeChannelIdentifier(searchTextLocal);
        const isChannelWithUsername = searchedBy === 'user';

        // First attempt - try direct channel lookup
        const res = await queryYouTubeApi({
            endpoint: '/channels',
            params: {
                id: !isChannelWithUsername && !newYoutubeUsername ? identifier : undefined,
                part: 'id,snippet,statistics',
                forUsername: isChannelWithUsername ? identifier.substring(1) : undefined,
                forHandle: newYoutubeUsername ? identifier : undefined,
            }
        });

        let channelData = res?.items || [];

        // Second attempt - try getting channel ID from URL if username search returned no results
        if (!channelData.length && isChannelWithUsername) {
            const channelId = await getChannelIdFromUrl(searchTextLocal);

            if (channelId) {
                const channelRes = await queryYouTubeApi({
                    endpoint: '/channels',
                    params: {
                        id: channelId,
                        part: 'id,snippet,statistics',
                    }
                });
                channelData = channelRes?.items || [];
            }
        }


        if (!channelData.length) {
            throw "No results found. Please enter a valid YouTube channel URL.";
        }

        return channelData;
    }

    // NOTE: this is purely for display purposes. It's not used for the API call and it's not saved to the database
    const formatSocialConnectData = (data: any) => {
        let newFormattedData: FormattedSocialConnectData | null = null;
        if (!data) {
            return null;
        }

        switch (socialConnectType) {
            case SocialConnectType.AUDIOMACK:
                newFormattedData = {
                    id: data.id,
                    title: data.name || '',
                    username: data.url_slug || '',
                    followers: data.followers || 0,
                    image: data.thumbnail_url || '',
                }
                break;
            case SocialConnectType.DEEZER:
                newFormattedData = {
                    id: data.id,
                    title: data.name || '',
                    username: '', // NOTE: Deezer doesn't have usernames
                    image: data.thumbnail_url || data.picture || '',
                    url: data.permalink_url || '',
                    followers: data.nb_fan || 0,
                }
                break;
            case SocialConnectType.FACEBOOK:
                newFormattedData = {
                    id: (data as FacebookPageType).id,
                    title: (data as FacebookPageType).name || '',
                    username: '',
                    followers: (data as FacebookPageType).followers_count || 0,
                    image: (data as FacebookPageType).picture?.data?.url || require('../../assets/images/profileIcon.svg').default,
                }
                break;
            case SocialConnectType.INSTAGRAM: {
                const username = data.username?.toLowerCase();
                newFormattedData = {
                    title: data.name || '',
                    username: `@${username}` || '',
                    followers: data.followers_count || 0,
                    image: data.profile_picture_url || '',
                    url: `https://www.instagram.com/${username}`,
                }
                break;
            }
            case SocialConnectType.SOUNDCLOUD:
                newFormattedData = {
                    id: data.id,
                    title: data.name || '',
                    username: `@${data.username || ''}`,
                    followers: data.followers || 0,
                    image: data.thumbnail_url || '',
                }
                break;
            case SocialConnectType.SPOTIFY:
                newFormattedData = {
                    id: data.id,
                    title: data.name || '',
                    username: '',
                    followers: data.followers || 0,
                    image: data.thumbnail_url || '',
                }
                break;
            case SocialConnectType.TIKTOK:
                newFormattedData = {
                    id: data.id,
                    title: data.name || '',
                    username: data.username || '',
                    followers: data.follower_count || 0,
                    image: data.picture || tikTokPicture,
                }
                break;
            case SocialConnectType.YOUTUBE:
                const {
                    title,
                    customUrl,
                    thumbnails,
                } = data?.snippet || {};

                newFormattedData = {
                    id: data.id,
                    title: title || '',
                    username: customUrl || '',
                    image: thumbnails?.medium?.url || '',
                    followers: data.statistics?.subscriberCount || 0,
                }
                break;
            default:
                return data;
        }

        return newFormattedData;
    }

    // NOTE: these platforms return a list of results
    const fetchSocialConnectListOnSearch = async () => {
        if (searchText.length < 2) {
            return;
        }

        setInputError(null);
        setIsLoadingSearchResults(true);

        try {
            let results: SocialConnectData[] | null = null;
            switch (socialConnectType) {
                case SocialConnectType.AUDIOMACK:
                    results = await fetchAudiomack();
                    break;
                case SocialConnectType.DEEZER:
                    results = await fetchDeezer();
                    break;
                case SocialConnectType.INSTAGRAM:
                    results = await fetchInstagramBusinessAccount();
                    break;
                case SocialConnectType.SOUNDCLOUD:
                    results = await fetchSoundCloud();
                    break;
                case SocialConnectType.SPOTIFY:
                    results = await fetchSpotify();
                    break;
                case SocialConnectType.TIKTOK:
                    const tikTokRes = await fetchTikTokBasic();
                    results = [tikTokRes];
                    break;
                case SocialConnectType.YOUTUBE:
                    results = await fetchYouTube();
                    break;
                default:
                    return null;
            }

            if (results.length === 0) {
                throw 'No results found. Please try again.';
            }

            setSearchResultsList(results);
        } catch (error: any) {
            setSearchResultsList([]);
            if (error) {
                setInputError(error.message ?? error);
            } else {
                setInputError('Error fetching social connect data');
            }
        } finally {
            setIsLoadingSearchResults(false);
        }
    }

    // NOTE: these platforms return a single result
    const fetchDirectSocialConnectData = async (): Promise<SocialConnectData | null> => {
        switch (socialConnectType) {
            case SocialConnectType.INSTAGRAM:
                return await fetchInstagramBasic(searchText);
            case SocialConnectType.TIKTOK:
                return await fetchTikTokBasic();
            default:
                return null;
        }
    }

    const getSpotifyToken = async () => { // NOTE CONNER: double check, might not need
        const { data, error } = await getNewSpotifyToken_api()
        if (error || !data?.token) {
            return null;
        }

        localStorage.setItem('spotify_token', data.token)

        return data.token
    }

    const transform_SpotifySearchData_To_SpotifyArtistInBrandConnections = async (spotifySearchData: SpotifyArtistSearchResponse) => {
        const searchTextLocal = searchText.trim().toLowerCase();
        if (!searchTextLocal.length) {
            return null;
        }

        const spotify_token = localStorage.getItem('spotify_token')
        try {
            const spotifyHeaders = {
                Authorization: 'Bearer ' + spotify_token
            }

            const spotifyArtistId = spotifySearchData.id
            const res = await axios.get(`https://api.spotify.com/v1/artists/${spotifyArtistId}`, { headers: spotifyHeaders })
            const transformedData = res.data as SpotifyArtistInBrandConnections

            return transformedData;
        } catch (e) {
            getSpotifyToken() // TODO CONNER: not sure if we need this
        }
    }

    const transformDataForConnectToBrand = async (data: SocialConnectData) => {
        switch (socialConnectType) {
            case SocialConnectType.SPOTIFY:
                return transform_SpotifySearchData_To_SpotifyArtistInBrandConnections(data as SpotifyArtistSearchResponse);
            default:
                return data;
        }
    }
    const connectToBrand = async ({
        isFetchDirectSocialData = false,
        dataFromList = null,
        formattedData = null
    }: {
        isFetchDirectSocialData?: boolean,
        dataFromList?: SocialConnectData | null,
        formattedData?: FormattedSocialConnectData | null
    }) => {
        if (!currentBrand) {
            return;
        }

        try {
            setIsLoading(true);
            let data: SocialConnectData | null = dataFromList;

            if (isFetchDirectSocialData) {
                data = await fetchDirectSocialConnectData();
            }

            const serviceNameForConnectToBrand = getServiceNameForConnectToBrand();

            if (socialConnectType === SocialConnectType.FACEBOOK) {
                setFacebookPage(data as FacebookPageType);
            } else if (isConnectToBrand) {
                const dataTransform = await transformDataForConnectToBrand(data as SocialConnectData);
                const updatedCurrentBrand = await connectToBrand_api({
                    brandId: Number(currentBrand.id),
                    brandSlug: String(currentBrand.slug),
                    service: serviceNameForConnectToBrand,
                    value: dataTransform,
                });
                setCurrentBrand(updatedCurrentBrand);
            }

            // Analytics tracking
            track('Linked Platform', {
                brand_id: currentBrand.id,
                brand_slug: currentBrand.slug,
                brand_name: currentBrand.name,
                platform: serviceNameForConnectToBrand,
                page: 'Dashboard',
                from: 'dashboard connect button'
            });
            setUserProperty(`${serviceNameForConnectToBrand}_linked`, true);

            toastSuccess(`${socialConnectTitle} connected!`);
            getDashboardData({ forceRefresh: true });

            if (onConnect) {
                onConnect(data as AnySocialConnectProfile);
            }

            onClose();
        } catch (error) {
            console.error('Error connecting account:', error);
            toastError('There was an error connecting your account. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }

    const isSelectedSpotifyArtistSameAsExisting = (id: string) => {
        const isSameSpotifyId = id === currentBrand?.connections?.spotify_artist_page?.id
        return isSameSpotifyId;
    }

    // NOTE: this is for the connect button on the list of results
    const handleListConnect = (
        result: SocialConnectData,
        formattedResult: FormattedSocialConnectData
    ) => {
        if (isConnectToBrand) {
            connectToBrand({
                dataFromList: result,
                formattedData: formattedResult
            });
        } else {
            onConnect && onConnect(result as AnySocialConnectProfile);
            onClose(); // Close the modal after successful connection
        }
    }

    // NOTE: this is for the direct connect button
    const handleDirectConnect = async () => {
        setSearchText(searchText.trim());

        if (!isConnectToBrand) {
            // For direct connect flow, fetch the data first
            const profileData = await fetchDirectSocialConnectData();
            if (profileData) {
                onConnect && onConnect(profileData as AnySocialConnectProfile);
                onClose(); // Close the modal after successful connection
            }
        } else {
            // Connect to brand flow
            connectToBrand({
                isFetchDirectSocialData: true
            });
        }
    }

    const getSocialConnectFooter = () => {
        if (socialConnectType === SocialConnectType.FACEBOOK) {
            if (facebookPages.length > 0) {
                return (
                    <div
                        className="socialConnectFooter"
                        style={sty.socialConnectFooter}
                    >
                        <p className="facebookPagesHelpText" style={sty.facebookPagesHelpText}>
                            Don't see your Facebook page listed? Make sure you've linked your page with Symphony by clicking below.
                            If you still don't see it, you may need to get access to the page.
                        </p>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 8,
                        }}>
                            <ButtonSymphony
                                variant="outlined"
                                width="100%"
                                className="linkPageButton"
                                onClick={reRequestPermissions}
                            >
                                Link a Page to Symphony &gt;
                            </ButtonSymphony>
                            <ButtonSymphony
                                variant="outlined"
                                width="100%"
                                className="pageAccessButton"
                                onClick={() => Intercom.openFacebookPageAccessArticle()}
                            >
                                How to get Facebook Page Access &gt;
                            </ButtonSymphony>
                        </div>
                    </div>
                )
            }
        }

        return (
            <div
                className="socialConnectFooter"
                style={sty.socialConnectFooter}
            >
                {socialConnectFooter}
            </div>
        )
    }

    const renderSocialConnectIcon = () => {
        switch (socialConnectType) {
            case SocialConnectType.AUDIOMACK:
                return (
                    <Icon
                        className="socialConnectIconAudiomack"
                        icon="audiomack"
                        size={ICON_SIZE}
                        style={sty.socialConnectIcon}
                    />
                );
            case SocialConnectType.DEEZER:
                return (
                    <Icon
                        className="socialConnectIconDeezer"
                        icon="deezer"
                        size={ICON_SIZE}
                        style={sty.socialConnectIcon}
                    />
                );
            case SocialConnectType.FACEBOOK:
                return (
                    <Icon
                        className="socialConnectIconFacebook"
                        icon="facebook"
                        size={ICON_SIZE}
                        style={sty.socialConnectIcon}
                    />
                );
            case SocialConnectType.INSTAGRAM:
                return (
                    <Icon
                        className="socialConnectIconInstagram"
                        icon="instagram"
                        size={ICON_SIZE}
                        style={sty.socialConnectIcon}
                    />
                );
            case SocialConnectType.SOUNDCLOUD:
                return (
                    <Icon
                        className="socialConnectIconSoundcloud"
                        icon="soundcloud"
                        size={ICON_SIZE}
                        style={sty.socialConnectIcon}
                    />
                );
            case SocialConnectType.SPOTIFY:
                return (
                    <Icon
                        className="socialConnectIconSpotify"
                        icon="spotify"
                        size={ICON_SIZE}
                        style={sty.socialConnectIcon}
                    />
                );
            case SocialConnectType.TIKTOK:
                return (
                    <Icon
                        className="socialConnectIconTiktok"
                        icon="tiktok"
                        size={ICON_SIZE}
                        style={sty.socialConnectIcon}
                    />
                );
            case SocialConnectType.YOUTUBE:
                return (
                    <Icon
                        className="socialConnectIconYoutube"
                        icon="youtube"
                        size={ICON_SIZE}
                        style={sty.socialConnectIcon}
                    />
                );
        }
    }

    const renderConnectButton = () => {
        return (
            <ButtonSymphony
                className="socialConnectButton"
                onClick={handleDirectConnect}
                width="100%"
                disabled={isLoading || !searchText || searchText.trim().length < 4}
                isLoading={isLoading}
                loadingText="Connecting..."
            >
                Connect
            </ButtonSymphony>
        )
    }

    const renderEmptyLoadingState = () => {
        return (
            <div className="socialConnectListEmptyLoadingState" style={sty.socialConnectListEmptyLoadingState}>
                <Loader />
            </div>
        )
    }

    const renderFollowersText = (followers: number) => {
        if (!followers) {
            return null;
        }

        const followersCount = millify(followers);
        let followersText = `${followersCount} ${serviceFollowActionName}`;
        return (
            <div
                className="socialConnectListItemRowFollowersText"
                style={{ color: Colors.greyDark }}
            >
                {followersText}
            </div>
        )
    }

    const renderConnectList = () => {
        // Handle loading states for both Facebook and other platforms
        if (isLoadingSearchResults || (socialConnectType === SocialConnectType.FACEBOOK && isLoadingPages)) {
            return renderEmptyLoadingState();
        }

        // Handle empty states for both Facebook and other platforms
        const listToRender = socialConnectType === SocialConnectType.FACEBOOK ?
            facebookPages : searchResultsList;

        if (!listToRender?.length) {
            return null;
        }

        return (
            <div className="socialConnectList" style={sty.socialConnectList}>
                {listToRender.map((result, index) => {
                    const formattedResult = formatSocialConnectData(result);
                    const {
                        id,
                        username,
                        title,
                        followers,
                        image,
                    } = formattedResult;

                    // Handle Facebook unpublished page state
                    const isUnpublishedFacebookPage = socialConnectType === SocialConnectType.FACEBOOK &&
                        !(result as FacebookPageType).is_published;

                    return (
                        <div
                            key={makeKey(id, index)}
                            className={clsx('socialConnectListItemRow', {
                                'unpublished': isUnpublishedFacebookPage
                            })}
                            style={sty.socialConnectListItemRow}
                        >
                            <img
                                className="socialConnectRomImage"
                                src={image}
                                alt={title}
                                style={sty.socialConnectRomImage}
                                onError={(e) => {
                                    e.currentTarget.src = require('../../assets/images/profileIcon.svg').default;
                                }}
                            />
                            <div className="socialConnectListItemRowLeft" style={sty.socialConnectListItemRowLeft}>
                                <div className="socialConnectListItemRowLeftTop" style={sty.socialConnectListItemRowLeftTop}>
                                    <div>
                                        {title}
                                    </div>
                                    {socialConnectType === SocialConnectType.FACEBOOK && (
                                        <div style={{ color: Colors.greyDark, marginLeft: 4 }}>
                                            [{(result as FacebookPageType).category}]
                                        </div>
                                    )}
                                </div>
                                {username && (
                                    <div style={{ color: Colors.greyDark, fontSize: 14 }}>
                                        {username}
                                    </div>
                                )}
                                {renderFollowersText(followers)}
                                {isUnpublishedFacebookPage && (
                                    <>
                                        <div style={{ color: Colors.red, fontSize: 12 }}>
                                            This Page is not published, and must be live before you can use it to start running ads.
                                        </div>
                                        <a
                                            style={{ color: Colors.red, fontSize: 12, textDecoration: 'underline' }}
                                            href={`https://www.facebook.com/${id}/settings/?tab=settings&ref=page_edit&section=page_visibility&view`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Click here to publish the page
                                        </a>
                                    </>
                                )}
                            </div>
                            <ButtonSymphony
                                className="socialConnectListItemButton"
                                style={sty.socialConnectListItemButton}
                                onClick={() => handleListConnect(result, formattedResult)}
                                disabled={isLoading || isUnpublishedFacebookPage}
                                isLoading={isLoading && currentlySelectedSearchResultId === id}
                                variant="text"
                            >
                                Connect
                            </ButtonSymphony>
                        </div>
                    )
                })}

            </div>
        )
    }

    const renderInputError = () => {
        if (inputError) {
            return (
                <div
                    className="socialConnectModalInputError"
                    style={sty.socialConnectModalInputError}
                >
                    {inputError}
                </div>
            )
        }
        return null;
    }

    const renderErrorComponent = () => {
        if (!isErrorComponentVisible) {
            return null;
        }

        switch (socialConnectType) {
            case SocialConnectType.INSTAGRAM:
                return <NotFoundInstaUser />;
            default:
                return null;
        }
    }

    const renderConnectInput = () => {
        if (socialConnectType === SocialConnectType.FACEBOOK) {
            if (shouldShowLoginButton()) {
                return renderFacebookLoginButton();
            } else {
                return null
            }
        }

        return (
            <div className="inputIconRow" style={sty.inputIconRow}>
                {renderSocialConnectIcon()}
                <InputSymphony
                    className="socialConnectInput"
                    style={sty.socialConnectInput}
                    placeholder={serviceInputPlaceholder}
                    value={searchText}
                    isLoading={isLoadingSearchResults}
                    disabled={isLoadingSearchResults}
                    onChange={(valueText) => setSearchText(valueText)}
                    isSearch={isConnectList}
                    onSearch={fetchSocialConnectListOnSearch}
                    onEnter={isDirectConnect ? handleDirectConnect : fetchSocialConnectListOnSearch}
                />
            </div>
        );
    }

    const renderConnectExtraContent = () => {
        switch (socialConnectType) {
            case SocialConnectType.SPOTIFY:
                if (isPlatformConnected('spotify')) {
                    return (
                        <div className="spotifyNotificationBanner" style={sty.spotifyNotificationBanner}>
                            When changing your Spotify, all of the releases from your new Spotify profile will be pulled into Symphony. Your old Spotify releases will be removed from our system. It might take a little bit to update.
                        </div>
                    )
                }
                return null;
        }
    }

    const renderConnectButtonOrSearch = () => {
        if (isConnectList) {
            return renderConnectList();
        }

        return renderConnectButton();
    }

    // TODO: render()
    return (
        <>
            <ModalSymphony
                className={clsx('socialConnectModalComponent', className)}
                style={styx(sty.socialConnectModalComponent, style)}
                isOpen={isOpen}
                onClose={onClose}
                title={socialConnectModalTitle}
                subtitle={socialConnectSubtitle}
                fullScreen={mobileView}
            >
                {renderConnectExtraContent()} {/* NOTE: this is optional extra content that can be added to the modal */}
                {renderConnectInput()}
                {renderInputError()}
                {renderErrorComponent()}

                {renderConnectButtonOrSearch()}

                {getSocialConnectFooter()}
            </ModalSymphony>

            {alreadyExistArtist && //brandToSwitchTo &&
                <AlreadyExistingArtistModal
                    className="dashboardAlreadyExistingArtistModal"
                    isOpen={openAlreadyExistModal}
                    onClose={() => setOpenAlreadyExistModal(false)}
                    artist={alreadyExistArtist}
                    buttonLabel=""
                />
            }
        </>
    )
}

const sty: Record<string, CSSProperties> = {
    socialConnectModalComponent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    socialConnectInput: {
        width: '100%',
        marginBottom: 2
    },
    socialConnectFooter: {
        fontSize: 14,
        color: Colors.greyDark,
    },
    socialConnectList: {
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 300,
        overflowY: 'auto',
        borderRadius: 8,
        boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.15)'
    },
    socialConnectListItemRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingLeft: 24,
        paddingRight: 24,
        paddingTop: 16,
        paddingBottom: 16,
        borderBottom: `1px solid ${Colors.greyLight}`,
        gap: 8,
    },
    socialConnectListItemRowLeft: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minWidth: 0,
    },
    socialConnectListItemRowLeftTop: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    socialConnectIcon: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        flexShrink: 0,
    },
    inputIconRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-end',
        gap: 8,
    },
    socialConnectRomImage: {
        height: 40,
        width: 40,
        borderRadius: 8,
        objectFit: 'cover',
        flexShrink: 0,
    },
    socialConnectListItemButton: {
        marginLeft: 'auto',
        flexShrink: 0,
        minWidth: 100,
    },
    socialConnectModalInputError: {
        color: Colors.red,
        fontSize: 14,
    },
    spotifyNotificationBanner: {
        padding: 16,
        backgroundColor: Colors.yellowLight,
        borderRadius: 16,
    },
    loginButton: {
        width: '100%',
        marginTop: 16,
    },
    socialConnectListEmptyLoadingState: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 16,
    },
    socialConnectListError: {
        padding: 16,
        textAlign: 'center',
    },
    reRequestPermissionsButton: {
        width: '100%',
        marginTop: 16,
        color: Colors.purple,
        fontWeight: 'bold',
        padding: 16,
        borderTop: `1px solid ${Colors.greyLight}`,
        borderBottom: `1px solid ${Colors.greyLight}`,
        textAlign: 'center',
    },
    createPageLink: {
        display: 'block',
        width: '100%',
        marginTop: 16,
        color: Colors.purple,
        fontWeight: 'bold',
        padding: 16,
        borderTop: `1px solid ${Colors.greyLight}`,
        borderBottom: `1px solid ${Colors.greyLight}`,
        textAlign: 'center',
        textDecoration: 'none',
    },
    facebookPagesHelp: {
        padding: 16,
        borderTop: `1px solid ${Colors.greyLight}`,
    },
    facebookPagesHelpText: {
        margin: '16px 0',
        padding: '0 20px',
    },
    linkPageButton: {
        width: '100%',
        color: Colors.purple,
        fontWeight: 600,
        padding: 16,
        borderTop: `1px solid ${Colors.greyLight}`,
        borderBottom: `1px solid ${Colors.greyLight}`,
        textAlign: 'center',
    },
    pageAccessButton: {
        width: '100%',
        color: Colors.purple,
        fontWeight: 600,
        padding: 16,
        borderTop: `1px solid ${Colors.greyLight}`,
        borderBottom: `1px solid ${Colors.greyLight}`,
        textAlign: 'center',
    },
}

export default SocialConnectModal;